import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import EntityName, { InvalidEntityNameError } from "legal-entities/entity-names/entity-name";
import { debounce } from "lodash";
import { useCallback, useState } from "react";

const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

interface EntityNameInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  entityName?: EntityName;
  label?: string;
  onChange?: (entityName?: EntityName, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export default function EntityNameInput(props: Readonly<EntityNameInputProps>) {
  const { idPrefix, className, variant, disabled, label, entityName, onChange, onKeyDown, ...otherProps } = props;

  const [entityNameFieldText, setEntityNameFieldText] = useState<string>(entityName?.value ?? "");
  const [entityNameFieldError, setEntityNameFieldError] = useState<string>("");

  const validateAndReport = useCallback(
    (nameValue?: string) => {
      let newEntityName: EntityName | undefined;
      let errorMessage: string = "";

      try {
        newEntityName = new EntityName(nameValue);
      } catch (error) {
        if (error instanceof InvalidEntityNameError) {
          errorMessage = error.message;
        } else {
          throw error;
        }
      } finally {
        setEntityNameFieldError(errorMessage);
        onChange?.(newEntityName, errorMessage);
      }
    },
    [onChange],
  );

  return (
    <InputField
      id={idPrefix ? idPrefix + "-entity-name-input" : "entity-name-input"}
      className={className}
      label={label ?? "Entity Name"}
      onChange={(event) => {
        const value = event.target.value;
        setEntityNameFieldText(value);
        debounce((value: string) => validateAndReport(value), 500)(value);
      }}
      required
      disabled={disabled}
      value={entityNameFieldText}
      error={!!entityNameFieldError}
      helperText={entityNameFieldError}
      variant={variant ?? "outlined"}
      autoComplete="organization"
      onKeyDown={onKeyDown}
      {...otherProps}
    />
  );
}
